/**
 * APTableLoadingState Component
 *
 * Loading state component for tables that displays skeleton rows or custom loading content.
 * Provides visual feedback while data is being fetched.
 *
 * @example
 * ```tsx
 * <APTableLoadingState
 *   loading={true}
 *   skeletonRows={5}
 *   columns={[
 *     { key: 'name', label: 'Name', accessor: 'name' },
 *     { key: 'email', label: 'Email', accessor: 'email' }
 *   ]}
 * />
 * ```
 */

import { forwardRef } from 'react';

import APTypography from '@shared/components/atoms/typography';

import type { IAPTableColumn, IAPTableLoadingState } from '../../types';

import './styles.scss';

export interface IAPTableLoadingStateProps extends IAPTableLoadingState {
  /**
   * Table columns (needed for skeleton structure)
   */
  columns: IAPTableColumn[];

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Test identifier for testing
   */
  'data-testid'?: string;
}

/**
 * APTableLoadingState - Loading state for tables
 *
 * Displays skeleton rows or custom loading content while
 * data is being fetched.
 */
export const APTableLoadingState = forwardRef<HTMLDivElement, IAPTableLoadingStateProps>(
  (
    {
      loading = false,
      // skeletonRows = 5, // Future enhancement for skeleton rows
      content,
      columns,
      className = '',
      'data-testid': testId = 'ap-table-loading-state',
      ...rest
    },
    ref
  ) => {
    if (!loading) {
      return null;
    }

    // Build loading state classes
    const loadingStateClasses = ['ap-table__loading-content', className].filter(Boolean).join(' ');

    // If custom content is provided, use that instead
    if (content) {
      return (
        <div ref={ref} className={loadingStateClasses} data-testid={testId} {...rest}>
          {content}
        </div>
      );
    }

    // Filter visible columns
    const visibleColumns = columns.filter((column) => column.visible !== false);
    const columnCount = visibleColumns.length;

    // Default loading content
    return (
      <tr className="ap-table__loading-row" data-testid={testId}>
        <td colSpan={columnCount} className="ap-table__loading-cell">
          <div ref={ref} className={loadingStateClasses} {...rest}>
            <div className="ap-table__loading-spinner" aria-hidden="true">
              <div className="ap-table__spinner"></div>
            </div>
            <APTypography as="p" size="base" color="muted">
              Loading...
            </APTypography>
          </div>
        </td>
      </tr>
    );
  }
);

APTableLoadingState.displayName = 'APTableLoadingState';

export default APTableLoadingState;
